import axiosInstance from './axiosInstance';
import type {
  LoginCredentials,
  RegisterCredentials,
  AuthResponse,
  ApiResponse,
} from '@/types';
import type {
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  UpdateProfileRequest,
  DeleteAccountRequest,
  LoginOtpRequiredResponse,
  RegisterOtpRequiredResponse,
} from '@/types/auth';
import { AUTH_ENDPOINTS } from '@/types/auth';

/**
 * Authentication API Service
 */
export const authService = {
  /**
   * Login user
   */
  login: async (
    credentials: LoginCredentials
  ): Promise<ApiResponse<AuthResponse | LoginOtpRequiredResponse>> => {
    const response = await axiosInstance.post(
      AUTH_ENDPOINTS.LOGIN,
      credentials
    );
    return response.data;
  },

  /**
   * Complete login after OTP verification (when backend sent requiresOtp)
   */
  verifyLoginOtp: async (tempToken: string, otp: string): Promise<ApiResponse<AuthResponse>> => {
    const response = await axiosInstance.post<ApiResponse<AuthResponse>>(
      AUTH_ENDPOINTS.VERIFY_LOGIN_OTP,
      { tempToken, otp }
    );
    return response.data;
  },

  /**
   * Register new user (may return requiresOtp + tempToken when OTP is sent)
   */
  register: async (
    credentials: RegisterCredentials
  ): Promise<ApiResponse<AuthResponse | RegisterOtpRequiredResponse>> => {
    const response = await axiosInstance.post(
      AUTH_ENDPOINTS.REGISTER,
      credentials
    );
    return response.data;
  },

  /**
   * Complete registration after OTP verification
   */
  verifyRegisterOtp: async (
    tempToken: string,
    otp: string
  ): Promise<ApiResponse<AuthResponse>> => {
    const response = await axiosInstance.post<ApiResponse<AuthResponse>>(
      AUTH_ENDPOINTS.VERIFY_REGISTER_OTP,
      { tempToken, otp }
    );
    return response.data;
  },

  /**
   * Logout user
   */
  logout: async (): Promise<ApiResponse<null>> => {
    const response = await axiosInstance.post<ApiResponse<null>>(
      '/auth/logout'
    );
    return response.data;
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<ApiResponse<AuthResponse>> => {
    const response = await axiosInstance.get<ApiResponse<AuthResponse>>(
      '/auth/profile'
    );
    return response.data;
  },

  /**
   * Refresh authentication token
   * The refresh token is sent automatically via HTTP-only cookie
   */
  refreshToken: async (): Promise<ApiResponse<{ token: string }>> => {
    const response = await axiosInstance.post<
      ApiResponse<{ token: string }>
    >('/auth/refresh-token', {});
    return response.data;
  },

  /**
   * Request password reset OTP sent to phone
   * Returns tempToken when OTP is sent (requiresOtp flow)
   */
  forgotPassword: async (
    data: ForgotPasswordRequest
  ): Promise<
    ApiResponse<null> &
      Partial<{
        requiresOtp: boolean;
        tempToken: string;
        message: string;
      }>
  > => {
    const response = await axiosInstance.post(
      '/auth/forgot-password',
      data
    );
    return response.data;
  },

  /**
   * Verify OTP from forgot-password flow; returns resetToken for reset-password page
   */
  verifyForgotOtp: async (
    tempToken: string,
    otp: string
  ): Promise<ApiResponse<{ resetToken: string }>> => {
    const response = await axiosInstance.post<
      ApiResponse<{ resetToken: string }>
    >(AUTH_ENDPOINTS.VERIFY_FORGOT_OTP, { tempToken, otp });
    return response.data;
  },

  /**
   * Reset password using token
   */
  resetPassword: async (
    data: ResetPasswordRequest
  ): Promise<ApiResponse<null>> => {
    const response = await axiosInstance.post<ApiResponse<null>>(
      '/auth/reset-password',
      data
    );
    return response.data;
  },

  /**
   * Change password for authenticated user
   */
  changePassword: async (
    data: ChangePasswordRequest
  ): Promise<ApiResponse<null>> => {
    const response = await axiosInstance.post<ApiResponse<null>>(
      '/auth/change-password',
      data
    );
    return response.data;
  },

  /**
   * Update user profile
   */
  updateProfile: async (
    data: UpdateProfileRequest
  ): Promise<ApiResponse<AuthResponse>> => {
    const response = await axiosInstance.put<ApiResponse<AuthResponse>>(
      '/auth/profile',
      data
    );
    return response.data;
  },

  /**
   * Request OTP to delete account.
   */
  requestDeleteAccountOtp: async (): Promise<
    ApiResponse<null> &
      Partial<{
        requiresOtp: boolean;
        message: string;
      }>
  > => {
    const response = await axiosInstance.post(AUTH_ENDPOINTS.DELETE_ACCOUNT_REQUEST_OTP);
    return response.data;
  },

  /**
   * Confirm delete account with OTP. Backend clears cookies on success.
   */
  confirmDeleteAccountOtp: async (data: DeleteAccountRequest): Promise<ApiResponse<null>> => {
    const response = await axiosInstance.post<ApiResponse<null>>(
      AUTH_ENDPOINTS.DELETE_ACCOUNT_CONFIRM_OTP,
      data
    );
    return response.data;
  },
};
